import { pathwayRegistry, getDrug, getFormulation, pgdRegistry } from "@/lib/pharmacyFirst";
import type {
  AnswerRecord,
  AuditRecord,
  Pathway,
  PathwayNode,
} from "@/lib/pharmacyFirst";
import type {
  PGDWeightManagementSummaryData,
  SummaryCheckRow,
} from "./PGDWeightManagementSummary";

/**
 * Builds the PGDWeightManagementSummary props from an AHT weight-management
 * AuditRecord. The summary has one fixed field set for all six drugs, so the
 * audit trail is mapped onto those named fields rather than rendered as raw
 * question text. Findings with no fixed field land in `additionalChecks`.
 */

type NodeIndex = Map<string, PathwayNode>;

/** "yes" / "no" / "" when the pathway never asked. */
type Tri = "yes" | "no" | "";

/** Node ids referenced by the field mapping below. */
const N = {
  groupA: "Q3_thyroid_cancer_men2",
  q6Insulin: "Q6_sulphonylureas_or_insulin",
  renalHepatic: "Q7_severe_renal_or_hepatic_impairment",
  groupB: "Q8_seizures_or_cns_tumour",
  eatingDisorder: "Q9a_eating_disorder",
  bipolar: "Q9b_bipolar",
  bpControlled: "Q12_bp_controlled_decision",
  suicidalIdeation: "Q13_active_suicidal_ideation",
  groupC: "Q14_severe_gi_or_gastroparesis",
  pregnancy: "Q15_pregnancy_breastfeeding_planning",
  groupD: "Q17_ciclosporin",
  interactions: "Q20_interactions_checklist",
  hba1c: "Q2b_hba1c_entry",
  faceToFaceAttestation: "Q1a_face_to_face_attestation",
  sharedRedFlags: "sa-q0-gateway",
  sharedCommitments: "sa-q3-consent",
  sharedExclusions: "sa-q7-bmi-floor",
} as const;

/** Turns "q7RenalHepatic" / "shared-bmi-check" into readable prose. */
function humanize(id: string): string {
  const spaced = id
    .replace(/[-_]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/**
 * The chain records its completed steps as `chainSections` when the audit is
 * saved and as `pathwaySections` in-engine; read both.
 */
function visitedPathwayIds(audit: AuditRecord): Set<string> {
  const ids = new Set<string>();
  if (audit.pathwayId) ids.add(audit.pathwayId);
  const sections = [
    ...(audit.pathwaySections ?? []),
    ...(((audit as any).chainSections ?? []) as { pathwayId: string }[]),
  ];
  for (const section of sections) {
    if (section?.pathwayId) ids.add(section.pathwayId);
  }
  return ids;
}

/**
 * Question text lives on the pathway node, not in the audit, and an AHT
 * consultation spans a chain of pathways. Index the pathways the chain
 * actually visited; with no section records, fall back to every AHT pathway so
 * labels still resolve.
 */
function buildNodeIndex(audit: AuditRecord): NodeIndex {
  const pathwayIds = visitedPathwayIds(audit);
  if (pathwayIds.size <= 1) {
    for (const id of Object.keys(pathwayRegistry)) {
      if (id.startsWith("aht-")) pathwayIds.add(id);
    }
  }

  const index: NodeIndex = new Map();
  for (const id of pathwayIds) {
    const pathway: Pathway | undefined = pathwayRegistry[id];
    if (!pathway) continue;
    for (const node of pathway.nodes) {
      if (!index.has(node.id)) index.set(node.id, node);
    }
  }
  return index;
}

/** Flag labels are declared per pathway; merge the chain's into one map. */
function buildFlagLabels(audit: AuditRecord): Record<string, string> {
  const labels: Record<string, string> = {};
  const pathwayIds = visitedPathwayIds(audit);
  for (const id of pathwayIds) {
    Object.assign(labels, pathwayRegistry[id]?.auditFlagLabels ?? {});
  }
  if (Object.keys(labels).length === 0) {
    for (const id of Object.keys(pathwayRegistry)) {
      if (id.startsWith("aht-")) {
        Object.assign(labels, pathwayRegistry[id]?.auditFlagLabels ?? {});
      }
    }
  }
  return labels;
}

function optionLabel(node: PathwayNode | undefined, id: string): string {
  const n = node as any;
  if (!n) return id;
  if (Array.isArray(n.options)) {
    const hit = n.options.find((o: { id: string }) => o.id === id);
    if (hit) return hit.label;
  }
  const items: { id: string; text: string }[] = n.groups
    ? n.groups.flatMap((g: { items: { id: string; text: string }[] }) => g.items)
    : (n.items ?? []);
  const item = items.find((i) => i.id === id);
  return item ? item.text : id;
}

/** Labels must be unique — they key the rendered rows. */
function dedupeLabels(rows: SummaryCheckRow[]): SummaryCheckRow[] {
  const seen = new Map<string, number>();
  return rows.map((row) => {
    const count = (seen.get(row.label) ?? 0) + 1;
    seen.set(row.label, count);
    return count === 1 ? row : { ...row, label: `${row.label} (${count})` };
  });
}

/**
 * Only the current step's answers live on the audit — the chain carries every
 * earlier step's in `_chainAuditAnswers`. Without merging the two, the summary
 * shows just the last pathway's handful of questions. Later answers win, so a
 * re-asked node reports what the clinician finally said.
 */
function collectAnswers(
  audit: AuditRecord,
  ctx: Record<string, any>
): AnswerRecord[] {
  const carried = Array.isArray(ctx._chainAuditAnswers)
    ? (ctx._chainAuditAnswers as AnswerRecord[])
    : [];
  const byNodeId = new Map<string, AnswerRecord>();
  for (const answer of [...carried, ...(audit.answers ?? [])]) {
    if (answer && typeof answer.nodeId === "string") {
      byNodeId.set(answer.nodeId, answer);
    }
  }
  return [...byNodeId.values()];
}

/** Q2a stores bare item ids; resolve them off the node that captured them. */
function comorbidityLabels(nodes: NodeIndex, ids: unknown): string[] {
  if (!Array.isArray(ids)) return [];
  const node = [...nodes.values()].find(
    (n) => (n as any).storesTo === "q2aComorbiditySelections"
  );
  return ids.map((id) => optionLabel(node, String(id)));
}

// ─── Evidence gathering ──────────────────────────────────────────────────────

interface Evidence {
  ctx: Record<string, any>;
  /** nodeId → answer, merged across the whole chain. */
  answers: Map<string, unknown>;
  /** Question text → answer. The per-drug pathways reuse wording, not ids. */
  texts: { text: string; answer: unknown }[];
  /** PGD exclusion / interaction criteria, whether each was triggered, and
   *  what the clinician literally answered (the inverse of `triggered` for
   *  inclusion criteria, which are phrased positively). */
  pgd: { checkId: string; triggered: boolean; answer: Tri }[];
  /** Counselling and caution text the pharmacist acknowledged at supply. */
  counselling: string;
}

function buildEvidence(
  audit: AuditRecord,
  nodes: NodeIndex,
  ctx: Record<string, any>,
  drugRef: string | undefined
): Evidence {
  const answers = new Map<string, unknown>();
  const texts: Evidence["texts"] = [];
  for (const record of collectAnswers(audit, ctx)) {
    answers.set(record.nodeId, record.answer);
    const text = (nodes.get(record.nodeId) as any)?.text;
    if (typeof text === "string") {
      texts.push({ text: text.toLowerCase(), answer: record.answer });
    }
  }

  // Advice rows are evidenced by what the clinician signed off: the supply
  // attestation statement, plus the PGD counselling points and cautions shown
  // on the supply screen behind the "all sections acknowledged" checkbox.
  const attested = (audit.attestationRecords ?? []).filter(
    (r) => r.attested && r.conclusion === "optionA"
  );
  const parts = attested.map((r) =>
    String((nodes.get(r.nodeId) as any)?.attestationStatement ?? "")
  );
  if (audit.supplyAcknowledgements?.allSectionsAcknowledged && drugRef) {
    const pgd = Object.values(pgdRegistry).find((p) => p.drugRef === drugRef);
    parts.push(...(pgd?.counselling ?? []));
    parts.push(...(pgd?.cautions ?? []).map((c: any) => `${c.text} ${c.adviceText ?? ""}`));
  }

  return {
    ctx,
    answers,
    texts,
    pgd: (audit.pgdExclusionAnswers ?? []).map((a) => {
      const check = (pgdRegistry[a.pgdId]?.preSupplyChecks ?? []).find(
        (c) => c.id === a.checkId
      );
      const saidYes =
        check?.direction === "exclude_on_no" ? !a.answered : a.answered;
      return {
        checkId: a.checkId,
        triggered: a.answered,
        answer: (saidYes ? "yes" : "no") as Tri,
      };
    }),
    counselling: parts.join("\n").toLowerCase(),
  };
}

function truthy(answer: unknown): Tri {
  if (answer === true || answer === "yes") return "yes";
  if (answer === false || answer === "no") return "no";
  return "";
}

/** A yes/no answer on a node the chain asked by id. */
function byNode(ev: Evidence, nodeId: string): Tri {
  return ev.answers.has(nodeId) ? truthy(ev.answers.get(nodeId)) : "";
}

/**
 * One item of a "select all that apply" node. A node that was answered without
 * the item selected is a definite No, not an unknown — that is what lets the
 * fixed rows report No rather than "Not specified".
 */
function byItem(
  ev: Evidence,
  ctxKey: string | null,
  nodeId: string,
  ...itemIds: string[]
): Tri {
  const fromCtx = ctxKey ? ev.ctx[ctxKey] : undefined;
  const fromAnswer = ev.answers.get(nodeId);
  const source = Array.isArray(fromCtx)
    ? fromCtx
    : Array.isArray(fromAnswer)
      ? fromAnswer
      : null;
  if (!source) return "";
  const ids = source.map(String);
  return itemIds.some((id) => ids.includes(id)) ? "yes" : "no";
}

/** A boolean the pathway stored into chain context via storesMany. Elimination
 *  nodes store the "yes"/"no" string form instead, so accept both. */
function byFlag(ev: Evidence, key: string): Tri {
  const value = ev.ctx[key];
  if (value === true || value === "yes") return "yes";
  if (value === false || value === "no") return "no";
  return "";
}

/** PGD criteria are keyed per drug ("ex-hypersensitivity-liraglutide"), so
 *  match on the shared prefix. */
function byPgd(ev: Evidence, prefix: string): Tri {
  const hits = ev.pgd.filter((c) => c.checkId.startsWith(prefix));
  if (hits.length === 0) return "";
  return hits.some((c) => c.triggered) ? "yes" : "no";
}

/** Last resort: the per-drug screening pathways reuse the same question
 *  wording under different node ids. */
function byText(ev: Evidence, pattern: RegExp): Tri {
  const hits = ev.texts.filter((t) => pattern.test(t.text));
  if (hits.length === 0) return "";
  if (hits.some((t) => truthy(t.answer) === "yes")) return "yes";
  return hits.some((t) => truthy(t.answer) === "no") ? "no" : "";
}

/** Advice is only claimed where the acknowledged text actually covers it. */
function byCounselling(ev: Evidence, pattern: RegExp): Tri {
  if (!ev.counselling) return "";
  return pattern.test(ev.counselling) ? "yes" : "";
}

/** A positive finding anywhere wins; a negative only counts when nothing
 *  positive was recorded. */
function resolve(...probes: Tri[]): Tri {
  if (probes.includes("yes")) return "yes";
  return probes.includes("no") ? "no" : "";
}

/**
 * Screening findings the fixed liraglutide field set has nowhere to put — the
 * Mysimba CNS criteria, orlistat's malabsorption criteria and so on. Without
 * these they would be silently dropped from a non-liraglutide summary.
 */
const EXTRA_CHECKS: {
  label: string;
  probe: (ev: Evidence) => Tri;
  tone?: SummaryCheckRow["tone"];
}[] = [
  {
    label: "MTC / MEN2 history",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q3Thyroid"),
        byItem(ev, "q3to5GroupASelections", N.groupA, "q3_thyroid"),
        byPgd(ev, "ex-thyroid-men2")
      ),
  },
  {
    label: "Type 1 / Type 3c diabetes",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q5T1dmT3c"),
        byItem(ev, "q3to5GroupASelections", N.groupA, "q5_t1dm_t3c"),
        byPgd(ev, "ex-t1dm-t3c")
      ),
  },
  {
    label: "Seizure disorder / CNS tumour",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q8SeizuresCns"),
        byItem(ev, "q8to11GroupBSelections", N.groupB, "q8_seizures_cns"),
        byPgd(ev, "ex-seizure-disorder"),
        byPgd(ev, "ex-cns-tumour")
      ),
  },
  {
    label: "Opioid use or withdrawal",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q10Opioids"),
        byItem(ev, "q8to11GroupBSelections", N.groupB, "q10_opioids"),
        byPgd(ev, "ex-chronic-opioids"),
        byPgd(ev, "ex-opioid-use-7-10-days"),
        byPgd(ev, "int-opioid-analgesics")
      ),
  },
  {
    label: "MAOI within 14 days",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q11Maois"),
        byItem(ev, "q8to11GroupBSelections", N.groupB, "q11_maois"),
        byPgd(ev, "ex-maoi-current-recent"),
        byPgd(ev, "int-maois")
      ),
  },
  {
    label: "Bipolar disorder",
    probe: (ev) => resolve(byNode(ev, N.bipolar), byPgd(ev, "ex-bipolar")),
  },
  {
    label: "Blood pressure controlled",
    // The PGD criterion is the inverse ("uncontrolled hypertension?"), so a
    // triggered criterion means blood pressure is NOT controlled.
    probe: (ev) =>
      resolve(
        byNode(ev, N.bpControlled),
        byPgd(ev, "ex-uncontrolled-hypertension") === "yes"
          ? "no"
          : byPgd(ev, "ex-uncontrolled-hypertension") === "no"
            ? "yes"
            : ""
      ),
    tone: "benefit",
  },
  {
    label: "Active suicidal ideation",
    probe: (ev) =>
      resolve(byNode(ev, N.suicidalIdeation), byPgd(ev, "ex-suicidal-ideation")),
  },
  {
    label: "Cholestasis / malabsorption",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q16Cholestasis"),
        byItem(ev, "q14to16GroupCSelections", N.groupC, "q16_cholestasis"),
        byPgd(ev, "ex-cholestasis"),
        byPgd(ev, "ex-chronic-malabsorption")
      ),
  },
  {
    label: "Ciclosporin",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q17Ciclosporin"),
        byItem(ev, "q17to19GroupDSelections", N.groupD, "q17_ciclosporin"),
        byPgd(ev, "ex-ciclosporin"),
        byPgd(ev, "int-ciclosporin")
      ),
  },
  {
    label: "Diabetic retinopathy",
    probe: (ev) =>
      resolve(
        byFlag(ev, "q18RetinopathyAny"),
        byItem(
          ev,
          "q17to19GroupDSelections",
          N.groupD,
          "q18_retinopathy",
          "q18_retinopathy_any"
        ),
        byPgd(ev, "ex-retinopathy-any")
      ),
  },
  {
    label: "Narrow therapeutic index medicines",
    probe: (ev) =>
      resolve(
        byItem(ev, "q20InteractionSelections", N.interactions, "narrow_ti_oral_medicines"),
        byPgd(ev, "int-narrow-therapeutic-index"),
        byText(ev, /narrow therapeutic index/)
      ),
  },
  {
    label: "CYP2D6 substrates",
    probe: (ev) =>
      resolve(byPgd(ev, "int-cyp2d6-substrates"), byText(ev, /cyp2d6 substrates/)),
  },
  {
    label: "Seizure-threshold lowering medicines",
    probe: (ev) =>
      resolve(
        byPgd(ev, "int-seizure-threshold-lowering"),
        byText(ev, /seizure threshold/)
      ),
  },
  {
    label: "CYP2B6 inducers",
    probe: (ev) =>
      resolve(
        byItem(ev, "q20InteractionSelections", N.interactions, "cyp2b6_inducers"),
        byPgd(ev, "int-cyp2b6-inducers"),
        byText(ev, /cyp2b6 enzyme inducers/)
      ),
  },
  {
    label: "CYP2B6 inhibitors",
    probe: (ev) =>
      byItem(ev, "q20InteractionSelections", N.interactions, "cyp2b6_inhibitors"),
  },
  {
    label: "Antiepileptic drugs",
    probe: (ev) =>
      resolve(
        byItem(ev, "q20InteractionSelections", N.interactions, "antiepileptic_drugs"),
        byPgd(ev, "int-antiepileptics"),
        byPgd(ev, "ex-antiepileptics-no-monitoring"),
        byPgd(ev, "int-valproate"),
        byText(ev, /antiepileptic drugs/)
      ),
  },
  {
    label: "Levothyroxine",
    probe: (ev) =>
      resolve(
        byItem(ev, "q20InteractionSelections", N.interactions, "levothyroxine"),
        byPgd(ev, "int-levothyroxine"),
        byText(ev, /levothyroxine/)
      ),
  },
  {
    label: "Tamoxifen",
    probe: (ev) =>
      resolve(
        byItem(ev, "q20InteractionSelections", N.interactions, "tamoxifen"),
        byPgd(ev, "int-tamoxifen")
      ),
  },
  {
    label: "Monitoring or review refused",
    probe: (ev) =>
      resolve(
        byFlag(ev, "sharedMonitoringRefusal"),
        byPgd(ev, "ex-bmi-monitoring-refusal")
      ),
  },
];

/**
 * PGD criteria a fixed row or an EXTRA_CHECKS entry already reports. Anything
 * not listed here falls through to `additionalChecks`, so keep this in step
 * with the probes above or the summary double-reports the same finding.
 */
const CONSUMED_PGD_PREFIXES = [
  // Inclusion criteria — reported by patientConsent / consultationMethod /
  // bmiEligibility / ageConfirmed rather than as standalone findings.
  "inc-",
  "ex-age-",
  "ex-adult-age-75-plus",
  "ex-adol-age-under-12",
  "ex-bmi-",
  "ex-adult-bmi-",
  "ex-adol-bmi-below-iotf",
  "ex-concurrent-",
  "ex-endocrine-",
  "ex-adol-eating-disorder",
  "ex-bulimia-anorexia",
  "ex-nyha-iv-heart-failure",
  "ex-t2dm-hba1c-high",
  "ex-hypersensitivity",
  "ex-anaphylaxis-glp1",
  "ex-lactose-intolerance",
  "ex-virtual-consultation",
  "ex-pancreatitis",
  "ex-pregnancy",
  "ex-severe-renal",
  "ex-end-stage-renal",
  "ex-severe-hepatic",
  "ex-severe-gi",
  "ex-sulphonylureas-insulin",
  "ex-thyroid-men2",
  "ex-t1dm-t3c",
  "ex-retinopathy-any",
  "ex-seizure-disorder",
  "ex-cns-tumour",
  "ex-chronic-opioids",
  "ex-opioid-use-7-10-days",
  "ex-maoi-current-recent",
  "ex-bipolar",
  "ex-suicidal-ideation",
  "ex-cholestasis",
  "ex-chronic-malabsorption",
  "ex-ciclosporin",
  "ex-uncontrolled-hypertension",
  "ex-antiepileptics-no-monitoring",
  "ex-oral-anticoagulants-no-inr",
  "int-concurrent-glp1",
  "int-glp1-incretin-concurrent",
  "int-other-weight-medicines",
  "int-insulin-sulphonylureas",
  "int-warfarin",
  "int-narrow-therapeutic-index",
  "int-opioid-analgesics",
  "int-maois",
  "int-ciclosporin",
  "int-cyp2d6-substrates",
  "int-cyp2b6-inducers",
  "int-seizure-threshold-lowering",
  "int-antiepileptics",
  "int-valproate",
  "int-levothyroxine",
  "int-tamoxifen",
  "int-oral-contraceptives",
];

export function buildAhtWeightSummaryData(
  audit: AuditRecord
): PGDWeightManagementSummaryData {
  const nodes = buildNodeIndex(audit);
  const flagLabels = buildFlagLabels(audit);
  const ctx = (audit.contextSnapshot ?? {}) as Record<string, any>;

  const drugRef =
    audit.drugResolution?.resolvedDrugId ?? audit.supplyDetails?.drugId ?? undefined;
  const drug = (() => {
    if (!drugRef) return null;
    try {
      return getDrug(drugRef);
    } catch {
      return null;
    }
  })();
  const pgd = drugRef
    ? (Object.values(pgdRegistry).find((p) => p.drugRef === drugRef) ?? null)
    : null;

  const ev = buildEvidence(audit, nodes, ctx, drugRef);
  const age = audit.patient?.ageYears;

  // ── Eligibility: builder's seventeen named rows ────────────────────────────
  const ageConfirmed: Tri = (() => {
    const excluded = resolve(
      byPgd(ev, "ex-adult-age-75-plus"),
      byPgd(ev, "ex-adol-age-under-12")
    );
    if (excluded === "yes") return "no";
    const range = pgd?.population?.ageRange;
    if (range && typeof age === "number") {
      return age >= range.min && age <= range.max ? "yes" : "no";
    }
    return excluded === "no" ? "yes" : "";
  })();

  const willingDietExercise = resolve(
    byFlag(ev, "sharedLifestyleAgreement"),
    byItem(ev, null, N.sharedCommitments, "sa-q5-lifestyle")
  );

  // One checklist item covers pregnancy, breastfeeding and planning, so both
  // rows read from it; Q15 asks the same question in the selector.
  const pregnancyOrBreastfeeding = resolve(
    byFlag(ev, "sharedPregnancyBreastfeeding"),
    byItem(ev, null, N.sharedExclusions, "sa-q8-pregnancy"),
    byItem(ev, null, N.sharedRedFlags, "rf-pregnancy"),
    byNode(ev, N.pregnancy),
    byPgd(ev, "ex-pregnancy")
  );

  const eligibility = {
    ageConfirmed,
    willingDietExercise,
    pregnant: pregnancyOrBreastfeeding,
    breastfeeding: pregnancyOrBreastfeeding,
    otherWeightLossMeds: resolve(
      byFlag(ev, "sharedConcurrentWeightAgent"),
      byItem(ev, null, N.sharedExclusions, "sa-q9-concurrent"),
      byPgd(ev, "ex-concurrent-"),
      byPgd(ev, "int-other-weight-medicines"),
      byPgd(ev, "int-concurrent-glp1"),
      byPgd(ev, "int-glp1-incretin-concurrent")
    ),
    historyPancreatitis: resolve(
      byFlag(ev, "q4Pancreatitis"),
      byItem(ev, "q3to5GroupASelections", N.groupA, "q4_pancreatitis"),
      byItem(ev, null, N.sharedRedFlags, "rf-pancreatitis"),
      byPgd(ev, "ex-pancreatitis")
    ),
    // No pathway node asks about gallstones — it is a counselling caution only.
    cholelithiasis: "" as Tri,
    heartFailureReduced: resolve(
      byFlag(ev, "q19NyhaIV"),
      byItem(ev, "q17to19GroupDSelections", N.groupD, "q19_nyha_iv"),
      byPgd(ev, "ex-nyha-iv-heart-failure")
    ),
    endocrinologicalObesity: resolve(
      byFlag(ev, "sharedEndocrineObesity"),
      byItem(ev, null, N.sharedExclusions, "sa-q10-endocrine"),
      byPgd(ev, "ex-endocrine-")
    ),
    eatingDisorder: resolve(
      byNode(ev, N.eatingDisorder),
      byPgd(ev, "ex-adol-eating-disorder"),
      byPgd(ev, "ex-bulimia-anorexia")
    ),
    // The PGD bundles weight-gain medicines into the endocrine criterion.
    weightGainMedication: byPgd(ev, "ex-endocrine-eating-disorder-obesity"),
    severeRenalImpairment: resolve(
      byFlag(ev, "q7SevereRenal"),
      byFlag(ev, "oralSemaEgfrBelow30"),
      byItem(
        ev,
        "q7RenalHepatic",
        N.renalHepatic,
        "severe_renal",
        "liraglutide_renal_threshold",
        "oral_sema_egfr_below_30"
      ),
      byPgd(ev, "ex-severe-renal"),
      byPgd(ev, "ex-end-stage-renal")
    ),
    hepaticImpairment: resolve(
      byFlag(ev, "q7SevereHepatic"),
      byItem(ev, "q7RenalHepatic", N.renalHepatic, "severe_hepatic"),
      byPgd(ev, "ex-severe-hepatic")
    ),
    insulinSulfonylureas: resolve(
      byNode(ev, N.q6Insulin),
      byFlag(ev, "q6SulphonylureaInsulin"),
      byPgd(ev, "int-insulin-sulphonylureas"),
      byPgd(ev, "ex-sulphonylureas-insulin"),
      byText(ev, /insulin or (a )?sulphonylurea/)
    ),
    poorlyControlledDiabetes: resolve(
      byPgd(ev, "ex-t2dm-hba1c-high"),
      byFlag(ev, "t2dmHba1cHigh"),
      // The pathway stores HbA1c as `hba1cMmolMol`; `hba1c` was never written.
      typeof ctx.hba1cMmolMol === "number"
        ? (ctx.hba1cMmolMol >= 86 ? "yes" : "no")
        : "",
      typeof ev.answers.get(N.hba1c) === "number"
        ? ((ev.answers.get(N.hba1c) as number) >= 86 ? "yes" : "no")
        : ""
    ),
    ibdGastroparesis: resolve(
      byFlag(ev, "q14SevereGi"),
      byItem(ev, "q14to16GroupCSelections", N.groupC, "q14_severe_gi"),
      byPgd(ev, "ex-severe-gi")
    ),
    hypersensitivity: resolve(
      byPgd(ev, "ex-hypersensitivity"),
      byPgd(ev, "ex-anaphylaxis-glp1"),
      byPgd(ev, "ex-lactose-intolerance")
    ),
  };

  // ── Cautions: builder's eight named rows, plus notes ───────────────────────
  const cautionNotes = [
    ...(audit.flagsSet ?? []).map((flag) => flagLabels[flag] ?? humanize(flag)),
    ...(audit.overrides ?? []).map(
      (o) => `Override — ${o.caution}: ${o.decision} (${o.reason})`
    ),
  ].join("; ");

  const cautions = {
    // No node captures resting heart rate, surgery dates, or contraceptive and
    // HRT use — they are counselling cautions rather than screening questions.
    tachycardia: "" as Tri,
    elderlyPatient: typeof age === "number" && age >= 65 ? ("yes" as Tri) : ("" as Tri),
    mildModerateRenal: byItem(ev, "q7RenalHepatic", N.renalHepatic, "moderate_renal"),
    // MTC/MEN2 is a hard exclusion, not this caution, so it reports under the
    // additional findings instead of being understated here.
    thyroidDisease: "" as Tri,
    warfarinUse: resolve(
      byItem(ev, "q20InteractionSelections", N.interactions, "warfarin_anticoagulants"),
      byPgd(ev, "int-warfarin"),
      byPgd(ev, "ex-oral-anticoagulants-no-inr"),
      byText(ev, /warfarin/)
    ),
    oralContraceptiveUse: resolve(
      byPgd(ev, "int-oral-contraceptives"),
      byText(ev, /oral contraceptive/)
    ),
    hrtUse: "" as Tri,
    upcomingSurgery: "" as Tri,
    cautionNotes,
  };

  // ── Initial assessment: builder's named free-text rows ─────────────────────
  const previousAttempts: string[] = [];
  const noteAttempt = (nodeId: string, yes: string, no: string) => {
    const tri = byNode(ev, nodeId);
    if (tri === "yes") previousAttempts.push(yes);
    else if (tri === "no") previousAttempts.push(no);
  };
  noteAttempt(
    "sa-reinit-q2-weight-loss-response",
    "≥5% weight loss documented on previous course",
    "No documented 5% weight loss on previous course"
  );
  noteAttempt(
    "sa-reinit-q3-within-12-months",
    "Presenting within 12 months of last dose",
    "Last dose more than 12 months ago"
  );
  noteAttempt(
    "sa-reinit-q4-weight-regain",
    "Documented weight regain since stopping",
    "No documented weight regain since stopping"
  );

  const mentalHealth: string[] = [];
  if (eligibility.eatingDisorder === "yes") mentalHealth.push("Eating disorder history");
  if (byNode(ev, N.bipolar) === "yes") mentalHealth.push("Bipolar disorder");
  if (
    resolve(
      byNode(ev, N.suicidalIdeation),
      byItem(ev, null, N.sharedRedFlags, "rf-suicidal-ideation")
    ) === "yes"
  ) {
    mentalHealth.push("Active suicidal ideation");
  }

  // Only genuine clinician assessments belong here — the supply attestation is
  // already evidenced by the outcome and the advice rows.
  const clinicalAssessment = (audit.assessmentRecords ?? [])
    .map((r) => (r.notes ? `${r.conclusionLabel} — ${r.notes}` : r.conclusionLabel))
    .filter(Boolean)
    .join("; ");

  // ── Advice: claimed only where the acknowledged text covers it ─────────────
  const advice = {
    giAdviceGiven: byCounselling(
      ev,
      /gastrointestinal|gi effects|nausea|vomiting|abdominal pain/
    ),
    dehydrationAdviceGiven: byCounselling(ev, /dehydrat|hydration|fluid intake/),
    contraceptionAdviceGiven: byCounselling(ev, /contracept/),
    penUsageDemonstrated: byCounselling(ev, /injection technique|demonstrate|pen /),
    dietExerciseAdviceGiven: resolve(
      willingDietExercise,
      byCounselling(ev, /diet|physical activity|exercise/)
    ),
    nhsWeightLossPlanDiscussed: byCounselling(
      ev,
      /nhs weight loss plan|digital weight management/
    ),
    pilProvided: resolve(
      byCounselling(ev, /patient information leaflet/),
      audit.patientEmailSent ? "yes" : ""
    ),
  };

  const followUpNotes = [
    audit.patientEmailSent
      ? `Summary emailed to ${audit.patientEmailSent.sentTo}`
      : "",
    audit.extendedSupply
      ? `Extended supply: ${audit.extendedSupply.monthsSupplied} month(s) — ${audit.extendedSupply.reason}`
      : "",
    ...(audit.managementPlanCaptures ?? []).map((p) =>
      p.patientAcknowledged ? `${p.planText} (acknowledged)` : p.planText
    ),
  ]
    .filter(Boolean)
    .join("; ");

  // ── Treatment selection: why this drug, and what the patient preferred ────
  const selectionRows: SummaryCheckRow[] = [];
  const resolution = audit.drugResolution;
  if (drug) selectionRows.push({ label: "Medicine selected", value: drug.name });
  if (resolution?.selectionReason) {
    selectionRows.push({ label: "Selection reason", value: resolution.selectionReason });
  }
  if (resolution?.eligibleAtResolution?.length) {
    selectionRows.push({
      label: "Eligible at selection",
      value: resolution.eligibleAtResolution
        .map((id) => {
          try {
            return getDrug(id).name;
          } catch {
            return id;
          }
        })
        .join(", "),
    });
  }
  if (audit.drugSelection?.overridden) {
    selectionRows.push({
      label: "Clinician override",
      value: audit.drugSelection.overrideReason || "Recommendation overridden",
    });
  }
  for (const capture of audit.preferenceCaptures ?? []) {
    selectionRows.push({ label: capture.questionText, value: capture.answerLabel });
  }

  // ── Findings with no fixed row ─────────────────────────────────────────────
  const additionalChecks: SummaryCheckRow[] = [];
  for (const check of EXTRA_CHECKS) {
    const tri = check.probe(ev);
    if (!tri) continue;
    additionalChecks.push({
      label: check.label,
      value: tri === "yes" ? "Yes" : "No",
      tone: check.tone ?? "risk",
    });
  }
  for (const exclusion of audit.pgdExclusionAnswers ?? []) {
    if (!exclusion.answered) continue;
    if (CONSUMED_PGD_PREFIXES.some((p) => exclusion.checkId.startsWith(p))) continue;
    // Report what the clinician actually said. For an inclusion criterion a
    // triggered check means they answered No, so "Yes" would invert it.
    const literal = ev.pgd.find((c) => c.checkId === exclusion.checkId)?.answer;
    additionalChecks.push({
      label: exclusion.questionText,
      value: literal === "no" ? "No" : "Yes",
      tone: exclusion.category === "exclusion" ? "risk" : undefined,
    });
  }

  // ── Outcome, measurements and supply ──────────────────────────────────────
  const outcomeType = audit.finalOutcome?.outcomeType;
  const outcome =
    outcomeType === "supply"
      ? "supply-made"
      : outcomeType === "cannot_supply"
        ? "excluded"
        : outcomeType && (outcomeType.includes("refer") || outcomeType === "emergency")
          ? "refer-clinician"
          : (outcomeType ?? "");

  const bmi = ctx.bmi ?? (audit.patient as any)?.bmi;
  const bmiEligibility =
    typeof bmi === "number" ? (bmi >= 30 ? "bmi-30-plus" : bmi >= 27 ? "bmi-27-30-comorbidity" : "") : "";

  const supply = audit.supplyDetails;
  // Never fall back to the formulation id — an internal slug like
  // "liraglutide-6mg-3ml-pen" is not a product name. Try the resolved drug as
  // well as the supply's own drugId, then settle for the drug name.
  const productBrand = (() => {
    const candidates = [drugRef, supply?.drugId].filter(Boolean) as string[];
    if (supply) {
      for (const id of candidates) {
        try {
          const formulation = getFormulation(id, supply.formulationId);
          if (formulation.brandExample) return formulation.brandExample;
        } catch {
          // wrong drug for this formulation — try the next candidate
        }
      }
    }
    // Not every formulation carries a brand example (Mysimba, orlistat), so
    // fall back to the drug's own name rather than leaving the row blank.
    for (const id of candidates) {
      try {
        return getDrug(id).name;
      } catch {
        // not a registered drug id — try the next candidate
      }
    }
    return "";
  })();

  // Consent is taken on the demographic banner of the first pathway in the
  // chain. Later steps start a fresh audit, so read the chain-carried flag too,
  // and treat a confirmed banner as consent — it cannot be confirmed without.
  const consentObtained =
    audit.consentObtained === true ||
    ctx._chainConsentObtained === true ||
    (!audit.consentRefused && !!audit.bannerConfirmedAt);
  const consentRefused =
    audit.consentRefused === true || ctx._chainConsentRefused === true;

  // The shared assessment requires attendance at registered premises, and the
  // PGD excludes virtual consultations — either one evidences face-to-face.
  const faceToFace = resolve(
    byFlag(ev, "sharedFaceToFace"),
    byItem(ev, null, N.sharedCommitments, "sa-q6-f2f"),
    byNode(ev, N.faceToFaceAttestation),
    byPgd(ev, "ex-virtual-consultation") === "no" ? "yes" : ""
  );

  const startedAt = audit.startedAt ? new Date(audit.startedAt) : null;

  const result: PGDWeightManagementSummaryData = {
    patientConsent: consentObtained ? "yes" : consentRefused ? "no" : "",
    // Nothing in the pathway asks whether the treatment is for the patient
    // themselves; the banner identifies the patient directly.
    treatmentForSelf: "",
    consultationDate: audit.startedAt ?? "",
    consultationTime: startedAt
      ? startedAt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
      : "",
    consultationMethod: faceToFace === "yes" ? "face-to-face" : "",
    height: ctx.heightCm != null ? String(ctx.heightCm) : "",
    weight: ctx.weightKg != null ? String(ctx.weightKg) : "",
    bmi: bmi != null ? String(bmi) : "",
    waistCircumference:
      (audit.patient as any)?.waistCircumferenceCm != null
        ? String((audit.patient as any).waistCircumferenceCm)
        : "",
    patientAge: age != null ? String(age) : "",
    bmiEligibility,
    comorbidities: comorbidityLabels(nodes, ctx.q2aComorbiditySelections),
    comorbidityOther: "",
    ...eligibility,
    ...cautions,
    causesOfWeightGain: "",
    lifestyleDietExercise:
      willingDietExercise === "yes"
        ? "Agreed to a reduced-calorie diet and increased physical activity"
        : "",
    previousWeightLossAttempts: previousAttempts.join("; "),
    mentalHealthFactors: mentalHealth.join(", "),
    otherDiseaseStates: clinicalAssessment,
    patientExpectations: "",
    currentMedications: Array.isArray(audit.patient?.currentMedications)
      ? audit.patient.currentMedications.join(", ")
      : "",
    currentDoseWeek: "",
    // Combination products report a ready-made label ("8mg/90mg × 1 morning"),
    // so only a bare number needs the unit appending.
    currentDose:
      typeof supply?.resolvedDoseMg === "number"
        ? `${supply.resolvedDoseMg}mg`
        : (supply?.resolvedDoseMg ?? ""),
    // Only the rescreen/transfer pathways ask the pharmacist to pick a dose
    // step; an initial supply resolves its band from the PGD instead, so fall
    // back to the band the supply step actually recorded. Without this the dose
    // row loses its PGD label and shows the bare milligrams.
    selectedDoseStep: ctx.selectedDoseStep
      ? String(ctx.selectedDoseStep)
      : (supply?.doseBandId ?? undefined),
    drugRef,
    quantitySupplied: supply?.quantity?.label ?? "",
    // The supply step records neither pack counts nor batch, expiry, injection
    // site or supervision — these rows stay "Not specified" until it does.
    pensSupplied: "",
    needlesSupplied: "",
    sharpsBinSupplied: "",
    batchNumber: "",
    expiryDate: "",
    productBrand,
    injectionSite: "",
    firstInjectionSupervised: "",
    ...advice,
    safetyNetting: (audit.finalOutcome?.safetyNetting ?? []).join("; "),
    followUpDate: "",
    followUpNotes,
    outcome,
    exclusionReason: outcome === "excluded" ? (audit.finalOutcome?.reason ?? "") : "",
    declineReason: "",
    referralReason: outcome === "refer-clinician" ? (audit.finalOutcome?.reason ?? "") : "",
    referralDestination: "",
    internalNotes: "",
    selectionRows: dedupeLabels(selectionRows),
    additionalChecks: dedupeLabels(additionalChecks),
  };

  return result;
}
